import { WORKSHEET_CSS } from './worksheetStyles';

export interface WorksheetMeta {
  title: string;
  subject?: string;
  grade?: string;
  schoolYear?: string;
  teacher?: string;
  font?: string; // --ws-font (Klassenstufe)
  lh?: string; // --ws-lh
  frame?: string; // Schmuckrahmen-Stil
  solutions?: boolean; // Lösungen anzeigen (true) oder als Leerlinie (false)
  family?: string; // Schriftart-Stack (--ws-family)
}

function esc(s: string | undefined): string {
  return (s || '').replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!),
  );
}

function buildDocument(content: string, meta: WorksheetMeta): string {
  const metaBits = [
    meta.subject ? `<span><b>Fach:</b> ${esc(meta.subject)}</span>` : '',
    meta.grade ? `<span><b>Klasse:</b> ${esc(meta.grade)}</span>` : '',
    meta.schoolYear ? `<span><b>Schuljahr:</b> ${esc(meta.schoolYear)}</span>` : '',
  ].filter(Boolean).join('');

  return `<!doctype html><html lang="de"><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(meta.title) || 'Arbeitsblatt'}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Andika:wght@400;700&family=Lexend:wght@400;500;700&family=Atkinson+Hyperlegible:wght@400;700&display=swap" rel="stylesheet" />
<style>
  /* Echtes DIN-A4-Format */
  @page { size: A4 portrait; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif;
    color: #1B1714; background: #EDE6D3;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }

  /* Bedienleiste (nur am Bildschirm, nie im Druck) */
  .no-print-bar {
    position: sticky; top: 0; z-index: 10;
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
    background: #2238B5; color: #fff; padding: 11px 18px;
    font-family: 'Manrope', system-ui, sans-serif;
  }
  .no-print-bar strong { font-size: 15px; }
  .no-print-bar button {
    background: #fff; color: #2238B5; border: 0; border-radius: 8px;
    padding: 8px 16px; font-weight: 700; font-size: 14px; cursor: pointer;
  }
  .no-print-bar .hint { font-size: 12.5px; opacity: 0.85; }

  /* A4-Seite als Vorschau am Bildschirm */
  .sheet {
    position: relative;
    max-width: 210mm; margin: 18px auto; padding: 18mm 16mm;
    background: #fff; box-shadow: 0 4px 24px rgba(0,0,0,0.12);
  }

  /* Rahmen: am Bildschirm ums Vorschaublatt, im Druck pro Seite wiederholt */
  .print-frame { position: absolute; inset: 10mm; pointer-events: none; box-sizing: border-box; }

  .sheet-head { border-bottom: 2px solid #2238B5; padding-bottom: 10px; margin-bottom: 6px; }
  .sheet-head h1 { font-size: 22px; font-weight: 700; color: #2238B5; margin: 0 0 6px; line-height: 1.15; }
  .sheet-meta { display: flex; gap: 22px; flex-wrap: wrap; font-size: 12px; color: #4A433D; }
  .sheet-student { display: flex; gap: 28px; font-size: 12px; color: #4A433D; margin: 8px 0 18px; }
  .sheet-student .line { flex: 1; border-bottom: 1px solid #8A8278; min-width: 80px; }
  .sheet-foot { margin-top: 24px; padding-top: 8px; border-top: 1px solid #D9D2C2; font-size: 10px; color: #B8B0A1; display: flex; justify-content: space-between; }

  ${WORKSHEET_CSS}

  /* Im Druck: nur die Seite, A4-Ränder über @page */
  @media print {
    body { background: #fff; }
    .no-print-bar { display: none !important; }
    .sheet { max-width: none; margin: 0; padding: 0; box-shadow: none; }
    /* fixed → der Rahmen wird auf JEDER Druckseite wiederholt */
    .print-frame { position: fixed; inset: 0; }
  }
</style></head>
<body>
  <div class="no-print-bar">
    <strong>📄 Arbeitsblatt (DIN A4)</strong>
    <button type="button" onclick="window.print()">Als PDF speichern / Drucken</button>
    <span class="hint">Im Dialog Ziel „Als PDF sichern" wählen.</span>
  </div>
  <div class="sheet">
    ${meta.frame && meta.frame !== 'none' ? `<div class="print-frame ws-frame--${meta.frame}"></div>` : ''}
    <div class="sheet-head">
      <h1>${esc(meta.title) || 'Arbeitsblatt'}</h1>
      ${metaBits ? `<div class="sheet-meta">${metaBits}</div>` : ''}
    </div>
    <div class="sheet-student">
      <span>Name: <span class="line"></span></span>
      <span>Datum: <span class="line" style="max-width:120px"></span></span>
    </div>
    <div class="ws-content${meta.solutions === false ? ' hide-solutions' : ''}" style="--ws-font:${meta.font || '15px'};--ws-lh:${meta.lh || '1.7'};--ws-family:${meta.family || "'Manrope', sans-serif"}">
      ${content || '<p style="color:#8A8278;font-style:italic">Kein Inhalt</p>'}
    </div>
    <div class="sheet-foot">
      <span>${esc(meta.teacher)}</span>
      <span>Erstellt mit SketchLessons</span>
    </div>
  </div>
  <script>
    // Automatisch den Druckdialog öffnen, sobald Schrift/Bilder geladen sind.
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.focus(); window.print(); } catch (e) {} }, 600);
    });
  </script>
</body></html>`;
}

// Öffnet das Arbeitsblatt als eigenständiges A4-Dokument in einem neuen Tab.
// Der Browser-Druckdialog erzeugt daraus ein echtes DIN-A4-PDF (Vektor, mehrseitig).
export function printWorksheet(content: string, meta: WorksheetMeta): Promise<void> {
  return new Promise((resolve) => {
    const html = buildDocument(content, meta);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const win = window.open(url, '_blank');
    if (!win) {
      URL.revokeObjectURL(url);
      alert('Bitte erlaube Pop-ups für diese Seite, damit das PDF-Dokument geöffnet werden kann.');
      resolve();
      return;
    }
    // URL später wieder freigeben (Dokument ist dann längst geladen).
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    resolve();
  });
}
